import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import { indexersApi } from "@/api/indexers";
import type {
  CreateIndexerRequest,
  IndexerAuthMethod,
  IndexerDownloadRewriteRow,
  IndexerPreset,
  IndexerQueryRewriteRow,
  IndexerRow,
  ProtocolKind,
} from "@/api/types";
import { TagPills } from "@/components/TagPills";
import { Button } from "@/components/ui/Button";
import { EmptyState } from "@/components/ui/EmptyState";
import { Field, NumberInput, Select, TextInput } from "@/components/ui/Field";
import { Modal } from "@/components/ui/Modal";
import { PageHeader } from "@/components/ui/PageHeader";
import { useToast } from "@/components/ui/Toast";

/// Display labels for the per-indexer auth method dropdown (#43).
/// Wire values are the canonical [`IndexerAuthMethod`] strings.
const AUTH_METHOD_OPTIONS: ReadonlyArray<{
  value: IndexerAuthMethod;
  label: string;
  hint: string;
}> = [
  {
    value: "query-apikey",
    label: "Query string (?apikey=)",
    hint: "Default. The legacy behaviour for every existing indexer.",
  },
  {
    value: "header-x-api-key",
    label: "Header: X-Api-Key",
    hint: "Sends the key as an `X-Api-Key` request header.",
  },
  {
    value: "header-bearer",
    label: "Header: Authorization: Bearer",
    hint: "Sends `Authorization: Bearer {api_key}`.",
  },
  {
    value: "header-basic",
    label: "Header: Authorization: Basic",
    hint: "Sends HTTP Basic auth with username 'apikey' and the API key as the password.",
  },
  {
    value: "none",
    label: "None (public indexer)",
    hint: "No auth applied — for indexers that don't require a key.",
  },
];

type EditingState =
  | { mode: "closed" }
  | { mode: "create" }
  | { mode: "edit"; row: IndexerRow };

type SearchesState = { open: false } | { open: true; row: IndexerRow };

type RewritesState = { open: false } | { open: true; row: IndexerRow };

type DownloadRewritesState =
  | { open: false }
  | { open: true; row: IndexerRow };

export function Indexers() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [editing, setEditing] = useState<EditingState>({ mode: "closed" });
  const [searches, setSearches] = useState<SearchesState>({ open: false });
  const [rewrites, setRewrites] = useState<RewritesState>({ open: false });
  const [downloadRewrites, setDownloadRewrites] =
    useState<DownloadRewritesState>({ open: false });
  const [testingId, setTestingId] = useState<number | null>(null);

  const { data: indexers, isLoading } = useQuery({
    queryKey: ["indexers"],
    queryFn: indexersApi.list,
  });

  const removeMut = useMutation({
    mutationFn: (id: number) => indexersApi.remove(id),
    onSuccess: () => {
      toast("Indexer removed", "success");
      qc.invalidateQueries({ queryKey: ["indexers"] });
    },
    onError: (e) => toast(e instanceof Error ? e.message : "Remove failed", "error"),
  });

  // POST /indexers/:id/reset — clears `auto_disabled_at`,
  // `backoff_until`, `consecutive_failures`, and flips `enabled` back
  // on. Surfaces as a per-row "Re-enable" button on disabled rows so
  // the user doesn't have to dig into the Edit modal to recover from
  // an auto-disable.
  const resetMut = useMutation({
    mutationFn: (id: number) => indexersApi.reset(id),
    onSuccess: (row) => {
      toast(`${row.name} re-enabled`, "success");
      qc.invalidateQueries({ queryKey: ["indexers"] });
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : "Re-enable failed", "error"),
  });

  const test = async (row: IndexerRow) => {
    setTestingId(row.id);
    try {
      const res = await indexersApi.test(row.id);
      toast(
        res.healthy
          ? `${row.name} is healthy`
          : `${row.name} unhealthy: ${res.message ?? "unknown"}`,
        res.healthy ? "success" : "error",
      );
      qc.invalidateQueries({ queryKey: ["indexers"] });
    } catch (e) {
      toast(e instanceof Error ? e.message : "Test failed", "error");
    } finally {
      setTestingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Indexers"
        description="Newznab/Torznab sources searched when grabbing a release."
        actions={<Button onClick={() => setEditing({ mode: "create" })}>Add indexer</Button>}
      />

      {isLoading ? (
        <p className="text-sm text-foreground/60">Loading…</p>
      ) : !indexers || indexers.length === 0 ? (
        <EmptyState
          title="No indexers configured"
          description="Add at least one Newznab or Torznab provider to enable search."
          action={<Button onClick={() => setEditing({ mode: "create" })}>Add indexer</Button>}
        />
      ) : (
        <div className="overflow-x-auto rounded border border-line">
          <table className="w-full text-sm">
            <thead className="bg-surface-2/50 text-left text-xs uppercase text-foreground/60">
              <tr>
                <th className="px-4 py-2 font-medium">Name</th>
                <th className="px-4 py-2 font-medium">Protocol</th>
                <th className="px-4 py-2 font-medium">URL</th>
                <th className="px-4 py-2 font-medium">Health</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {indexers.map((row) => (
                <tr key={row.id} className="border-t border-line align-top">
                  <td className="px-4 py-2 font-medium">
                    <div className="flex flex-col gap-1.5">
                      <div>
                        {row.name}
                        {!row.enabled && (
                          <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase text-foreground/60">
                            disabled
                          </span>
                        )}
                      </div>
                      <TagPills target={{ kind: "indexer", id: row.id }} showEmpty />
                    </div>
                  </td>
                  <td className="px-4 py-2 capitalize text-foreground/70">{row.protocol}</td>
                  <td className="px-4 py-2 font-mono text-xs text-foreground/60">
                    {row.base_url}
                  </td>
                  <td className="px-4 py-2 text-xs">
                    {row.healthy === null ? (
                      <span className="text-foreground/40">untested</span>
                    ) : row.healthy ? (
                      <span className="text-green-400">healthy</span>
                    ) : (
                      <span className="text-red-400">unhealthy</span>
                    )}
                  </td>
                  <td className="space-x-2 px-4 py-2 text-right">
                    {!row.enabled && (
                      <Button
                        variant="primary"
                        size="sm"
                        loading={resetMut.isPending && resetMut.variables === row.id}
                        onClick={() => resetMut.mutate(row.id)}
                        title="Clear auto-disable + failure-streak state and re-enable the indexer"
                      >
                        Re-enable
                      </Button>
                    )}
                    <Button
                      variant="secondary"
                      size="sm"
                      loading={testingId === row.id}
                      onClick={() => test(row)}
                    >
                      Test
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setSearches({ open: true, row })}
                    >
                      Searches
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setRewrites({ open: true, row })}
                    >
                      Rewrites
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setDownloadRewrites({ open: true, row })}
                    >
                      DL rewrites
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setEditing({ mode: "edit", row })}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => {
                        if (confirm(`Remove indexer "${row.name}"?`)) removeMut.mutate(row.id);
                      }}
                    >
                      Remove
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <IndexerFormModal editing={editing} onClose={() => setEditing({ mode: "closed" })} />
      <IndexerSearchesModal
        state={searches}
        onClose={() => setSearches({ open: false })}
      />
      <IndexerRewritesModal
        state={rewrites}
        onClose={() => setRewrites({ open: false })}
      />
      <IndexerDownloadRewritesModal
        state={downloadRewrites}
        onClose={() => setDownloadRewrites({ open: false })}
      />
    </div>
  );
}

interface FormState {
  name: string;
  base_url: string;
  api_key: string;
  protocol: ProtocolKind;
  enabled: boolean;
  priority: number;
  categories: string; // CSV of newznab numeric category ids
  // Per-indexer User-Agent override (#44). Empty string = use the
  // global default. Surfaced in the form as a free-form text input
  // because tracker UA requirements are vendor-specific and we can't
  // know them up front.
  user_agent: string;
  // Per-indexer auth method (#43). Defaults to `query-apikey` so
  // freshly-added indexers behave like every existing row.
  auth_method: IndexerAuthMethod;
}

const blankForm = (): FormState => ({
  name: "",
  base_url: "",
  api_key: "",
  protocol: "usenet",
  enabled: true,
  priority: 25,
  categories: "",
  user_agent: "",
  auth_method: "query-apikey",
});

const formFromRow = (row: IndexerRow): FormState => {
  let cats: number[] = [];
  try {
    const parsed = JSON.parse(row.categories);
    if (Array.isArray(parsed)) cats = parsed.filter((n) => typeof n === "number");
  } catch {
    /* malformed — leave empty */
  }
  return {
    name: row.name,
    base_url: row.base_url,
    // API key isn't returned by GET — leave blank, treat as "keep existing".
    api_key: "",
    protocol: row.protocol,
    enabled: row.enabled,
    priority: row.priority,
    categories: cats.join(","),
    user_agent: row.user_agent ?? "",
    auth_method: row.auth_method ?? "query-apikey",
  };
};

const parseCategories = (csv: string): number[] =>
  csv
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => Number(s))
    .filter((n) => Number.isFinite(n));

const toCreate = (f: FormState): CreateIndexerRequest => ({
  name: f.name,
  base_url: f.base_url,
  api_key: f.api_key.trim() ? f.api_key : null,
  protocol: f.protocol,
  enabled: f.enabled,
  priority: f.priority,
  categories: parseCategories(f.categories),
});

type CreateTab = "preset" | "custom";

function IndexerFormModal({
  editing,
  onClose,
}: {
  editing: EditingState;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [form, setForm] = useState<FormState>(blankForm());
  const [submitting, setSubmitting] = useState(false);
  const [testing, setTesting] = useState(false);
  // Two-step "create" flow: pick a preset → form pre-fills → enter
  // API key. Edit mode skips this entirely (always custom).
  const [tab, setTab] = useState<CreateTab>("preset");
  const [pickedPresetId, setPickedPresetId] = useState<string | null>(null);

  const open = editing.mode !== "closed";
  const isEdit = editing.mode === "edit";
  const editingRow = editing.mode === "edit" ? editing.row : null;

  // Re-seed when the modal transitions or the editing target changes.
  useEffect(() => {
    if (editing.mode === "create") {
      setForm(blankForm());
      setTab("preset");
      setPickedPresetId(null);
    }
    if (editing.mode === "edit") {
      setForm(formFromRow(editing.row));
      setTab("custom");
      setPickedPresetId(null);
    }
  }, [editing]);

  // Pre-fill the create form from a preset, then jump straight to the
  // "custom" form view so the user finishes by pasting the API key.
  const applyPreset = (preset: IndexerPreset) => {
    setForm({
      name: preset.name,
      base_url: preset.base_url,
      api_key: "",
      protocol: preset.protocol,
      enabled: true,
      priority: 25,
      categories: preset.default_categories.join(","),
      user_agent: "",
      auth_method: "query-apikey",
    });
    setPickedPresetId(preset.id);
    setTab("custom");
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (isEdit && editingRow) {
        // Only send api_key if the user typed a new one — undefined keeps
        // the stored value via the backend's COALESCE-style PATCH.
        // user_agent is always sent: the empty string is the explicit
        // "use the global default" signal and the backend interprets it
        // as a NULL write.
        await indexersApi.update(editingRow.id, {
          name: form.name,
          base_url: form.base_url,
          api_key: form.api_key.trim() ? form.api_key : undefined,
          enabled: form.enabled,
          priority: form.priority,
          categories: parseCategories(form.categories),
          protocol: form.protocol,
          user_agent: form.user_agent,
          auth_method: form.auth_method,
        });
        toast("Indexer updated", "success");
      } else {
        await indexersApi.create(toCreate(form));
        toast("Indexer added", "success");
      }
      qc.invalidateQueries({ queryKey: ["indexers"] });
      onClose();
    } catch (err) {
      toast(err instanceof Error ? err.message : "Save failed", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const runTest = async () => {
    setTesting(true);
    try {
      const res = await indexersApi.testConfig(toCreate(form));
      toast(
        res.healthy
          ? "Indexer is healthy"
          : `Indexer unhealthy: ${res.message ?? "unknown"}`,
        res.healthy ? "success" : "error",
      );
    } catch (err) {
      toast(err instanceof Error ? err.message : "Test failed", "error");
    } finally {
      setTesting(false);
    }
  };

  const apiKeyPlaceholder = isEdit
    ? "leave blank to keep existing"
    : "optional for some indexers";

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? `Edit ${editingRow?.name ?? "indexer"}` : "Add indexer"}
      width="lg"
    >
      {!isEdit && (
        <div className="mb-4 flex gap-1 border-b border-line">
          <TabButton active={tab === "preset"} onClick={() => setTab("preset")}>
            Preset
          </TabButton>
          <TabButton active={tab === "custom"} onClick={() => setTab("custom")}>
            Custom
          </TabButton>
        </div>
      )}

      {!isEdit && tab === "preset" ? (
        <PresetPicker onPick={applyPreset} />
      ) : (
        <form onSubmit={submit} className="space-y-4">
          {pickedPresetId && (
            <div className="rounded border border-line bg-surface-2/40 px-3 py-2 text-xs text-foreground/70">
              Pre-filled from preset{" "}
              <span className="font-medium text-foreground/90">{form.name}</span>
              . Enter the API key below to finish.{" "}
              <button
                type="button"
                onClick={() => {
                  setPickedPresetId(null);
                  setTab("preset");
                }}
                className="underline hover:text-foreground"
              >
                Pick a different preset
              </button>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <Field label="Name">
              <TextInput
                required
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g. NZBgeek"
              />
            </Field>
            <Field label="Protocol">
              <Select
                value={form.protocol}
                onChange={(e) =>
                  setForm((f) => ({ ...f, protocol: e.target.value as ProtocolKind }))
                }
              >
                <option value="usenet">Usenet (Newznab)</option>
                <option value="torrent">Torrent (Torznab)</option>
              </Select>
            </Field>
          </div>
          <Field
            label="Base URL"
            hint="The Newznab/Torznab API endpoint. NZBGeek: https://api.nzbgeek.info/api"
          >
            <TextInput
              required
              value={form.base_url}
              onChange={(e) => setForm((f) => ({ ...f, base_url: e.target.value }))}
              placeholder="https://api.example.com/api"
            />
          </Field>
          <Field label="API key" hint={isEdit ? "leave blank to keep existing" : undefined}>
            <TextInput
              value={form.api_key}
              placeholder={apiKeyPlaceholder}
              onChange={(e) => setForm((f) => ({ ...f, api_key: e.target.value }))}
            />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Priority" hint="Lower number = tried first.">
              <NumberInput
                value={form.priority}
                onChange={(e) =>
                  setForm((f) => ({ ...f, priority: Number(e.target.value) || 0 }))
                }
              />
            </Field>
            <Field
              label="Categories"
              hint="Comma-separated newznab category ids. Empty = all."
            >
              <TextInput
                value={form.categories}
                placeholder="2000,5000"
                onChange={(e) => setForm((f) => ({ ...f, categories: e.target.value }))}
              />
            </Field>
          </div>
          <Field
            label="User-Agent override"
            hint="Leave blank to use the global default. Some private trackers require a specific UA like 'Sonarr/4.0'."
          >
            <TextInput
              value={form.user_agent}
              placeholder="Sonarr/4.0"
              spellCheck={false}
              onChange={(e) => setForm((f) => ({ ...f, user_agent: e.target.value }))}
            />
          </Field>
          <Field
            label="Auth method"
            hint={
              AUTH_METHOD_OPTIONS.find((o) => o.value === form.auth_method)?.hint ??
              "How the API key is delivered to the indexer."
            }
          >
            <Select
              value={form.auth_method}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  auth_method: e.target.value as IndexerAuthMethod,
                }))
              }
            >
              {AUTH_METHOD_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </Field>
          {/* Enabled toggle. The form state has always supported the
              field; this surfaces it so the user can flip it manually
              (e.g. to pause an indexer without removing it). For the
              recovery case after auto-disable, the per-row "Re-enable"
              button is the more direct path. */}
          <Field
            label="Enabled"
            hint="When off, search + auto-grab skip this indexer entirely. Tagged 'disabled' in the list. The Re-enable button on the indexers list does the same thing plus clears the failure-streak state."
          >
            <label className="flex cursor-pointer items-center gap-2 rounded border border-line bg-surface/40 px-3 py-2 text-sm">
              <input
                type="checkbox"
                className="cursor-pointer"
                checked={form.enabled}
                onChange={(e) =>
                  setForm((f) => ({ ...f, enabled: e.target.checked }))
                }
              />
              <span>{form.enabled ? "Enabled" : "Disabled"}</span>
            </label>
          </Field>
          <div className="flex items-center justify-between border-t border-line pt-3">
            <Button type="button" variant="secondary" onClick={runTest} loading={testing}>
              Test connection
            </Button>
            <div className="flex gap-2">
              <Button type="button" variant="ghost" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" loading={submitting}>
                {isEdit ? "Save changes" : "Add"}
              </Button>
            </div>
          </div>
        </form>
      )}
    </Modal>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        "px-4 py-2 text-sm font-medium transition " +
        (active
          ? "border-b-2 border-accent text-foreground"
          : "border-b-2 border-transparent text-foreground/60 hover:text-foreground")
      }
    >
      {children}
    </button>
  );
}

/**
 * One-click preset library (#34) — the first stop of the "Add indexer"
 * flow. Cards render the curated list from `GET /api/v1/indexer-presets`;
 * picking one pre-fills the create form and the parent modal flips to
 * the "Custom" tab where the user just enters the API key.
 */
function PresetPicker({ onPick }: { onPick: (p: IndexerPreset) => void }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["indexer-presets"],
    queryFn: indexersApi.presets,
    staleTime: 60 * 60 * 1000,
  });

  if (isLoading) {
    return <p className="text-sm text-foreground/60">Loading presets…</p>;
  }
  if (error || !data) {
    return (
      <p className="text-sm text-red-400">
        {error instanceof Error ? error.message : "Failed to load presets"}
      </p>
    );
  }
  if (data.length === 0) {
    return <EmptyState title="No presets available" description="" />;
  }

  return (
    <div className="space-y-3">
      <p className="text-xs text-foreground/60">
        Pick a well-known indexer to pre-fill the URL, protocol, and default
        categories — you'll only need to enter the API key. Or switch to the{" "}
        <span className="font-medium">Custom</span> tab to configure one
        manually.
      </p>
      <div className="grid max-h-[60vh] grid-cols-1 gap-3 overflow-y-auto pr-1 sm:grid-cols-2">
        {data.map((preset) => (
          <PresetCard key={preset.id} preset={preset} onPick={() => onPick(preset)} />
        ))}
      </div>
    </div>
  );
}

function PresetCard({
  preset,
  onPick,
}: {
  preset: IndexerPreset;
  onPick: () => void;
}) {
  return (
    <div className="flex flex-col gap-2 rounded border border-line bg-surface-2/30 p-3 text-sm">
      <div className="flex items-start justify-between gap-2">
        <div className="font-medium">{preset.name}</div>
        <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase text-foreground/60">
          {preset.protocol}
        </span>
      </div>
      <p className="text-xs text-foreground/60 leading-snug">
        {preset.description}
      </p>
      <div className="mt-auto flex items-center justify-between pt-1">
        <a
          href={preset.website}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-foreground/60 underline hover:text-foreground"
          onClick={(e) => e.stopPropagation()}
        >
          Website
        </a>
        <Button size="sm" onClick={onPick}>
          Use
        </Button>
      </div>
    </div>
  );
}

/**
 * Per-indexer query history dashboard (#41).
 *
 * Shows the 24h aggregate header + a table of recent searches. The
 * canonical use case is "NZBGeek returned 0 results for the last 50
 * queries" — surfaced via the zero_result_count stat plus per-row
 * status / hit columns. Refresh queries when the modal opens so the
 * data is fresh on each visit.
 */
function IndexerSearchesModal({
  state,
  onClose,
}: {
  state: SearchesState;
  onClose: () => void;
}) {
  const open = state.open;
  const indexerId = state.open ? state.row.id : null;
  const indexerName = state.open ? state.row.name : "";

  const stats = useQuery({
    queryKey: ["indexer-stats", indexerId],
    queryFn: () => indexersApi.stats(indexerId as number),
    enabled: open && indexerId !== null,
    refetchOnWindowFocus: false,
  });

  const list = useQuery({
    queryKey: ["indexer-searches", indexerId],
    queryFn: () => indexersApi.searches(indexerId as number, 100),
    enabled: open && indexerId !== null,
    refetchOnWindowFocus: false,
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Recent searches — ${indexerName}`}
      width="xl"
    >
      <div className="space-y-4">
        <StatsHeader
          loading={stats.isLoading}
          total={stats.data?.total_queries ?? 0}
          successRate={stats.data?.success_rate ?? 0}
          avgMs={stats.data?.avg_response_ms ?? 0}
          zeroResults={stats.data?.zero_result_count ?? 0}
        />

        {list.isLoading ? (
          <p className="text-sm text-foreground/60">Loading…</p>
        ) : !list.data || list.data.length === 0 ? (
          <EmptyState
            title="No searches yet"
            description="Trigger one to populate."
          />
        ) : (
          <div className="overflow-x-auto rounded border border-line">
            <table className="w-full text-sm">
              <thead className="bg-surface-2/50 text-left text-xs uppercase text-foreground/60">
                <tr>
                  <th className="px-3 py-2 font-medium">When</th>
                  <th className="px-3 py-2 font-medium">Query</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 text-right font-medium">Hits</th>
                  <th className="px-3 py-2 text-right font-medium">Time</th>
                </tr>
              </thead>
              <tbody>
                {list.data.map((r) => (
                  <tr key={r.id} className="border-t border-line align-top">
                    <td className="px-3 py-2 font-mono text-xs text-foreground/70">
                      {formatTimestamp(r.started_at)}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">
                      {r.query || (
                        <span className="text-foreground/40">(empty)</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      <StatusCell row={r} />
                    </td>
                    <td className="px-3 py-2 text-right text-xs tabular-nums">
                      {r.releases_returned ?? "—"}
                    </td>
                    <td className="px-3 py-2 text-right text-xs tabular-nums text-foreground/70">
                      {formatResponseTime(r.started_at, r.finished_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Modal>
  );
}

function StatsHeader({
  loading,
  total,
  successRate,
  avgMs,
  zeroResults,
}: {
  loading: boolean;
  total: number;
  successRate: number;
  avgMs: number;
  zeroResults: number;
}) {
  if (loading) {
    return (
      <p className="text-sm text-foreground/60">Loading 24h stats…</p>
    );
  }
  if (total === 0) {
    return (
      <p className="text-sm text-foreground/60">
        Last 24h: no queries yet.
      </p>
    );
  }
  const pct = Math.round(successRate * 100);
  const ms = Math.round(avgMs);
  return (
    <p className="text-sm text-foreground/80">
      Last 24h:{" "}
      <span className="font-medium">{total}</span> queries ·{" "}
      <span className={pct >= 95 ? "text-green-400" : pct >= 80 ? "text-amber-400" : "text-red-400"}>
        {pct}% success
      </span>{" "}
      ·{" "}
      <span className="text-foreground/70">{ms}ms avg</span> ·{" "}
      <span className={zeroResults > 0 ? "text-amber-400" : "text-foreground/70"}>
        {zeroResults} zero-result
      </span>
    </p>
  );
}

function StatusCell({
  row,
}: {
  row: { status_code: number | null; finished_at: string | null; error: string | null };
}) {
  if (row.finished_at === null) {
    return <span className="text-foreground/40">in flight</span>;
  }
  if (row.error) {
    return (
      <span title={row.error} className="text-red-400">
        {row.status_code ? `HTTP ${row.status_code}` : "error"}
      </span>
    );
  }
  return <span className="text-green-400">200</span>;
}

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

function formatResponseTime(
  started: string,
  finished: string | null,
): string {
  if (!finished) return "—";
  const a = new Date(started).getTime();
  const b = new Date(finished).getTime();
  if (Number.isNaN(a) || Number.isNaN(b)) return "—";
  const ms = Math.max(0, b - a);
  return `${ms}ms`;
}

/**
 * Per-indexer query-rewrites modal (#38).
 *
 * Each row is an ordered (pattern → replacement) regex applied to the
 * outbound query just before the indexer call. The "Test" affordance
 * at the bottom shows what an arbitrary query would become after the
 * full rule chain runs — the same chain `IndexerService::search` runs
 * on the wire (rules in `ord` ascending). Bad regexes are rejected
 * server-side at insert time so they can never land in the table.
 */
function IndexerRewritesModal({
  state,
  onClose,
}: {
  state: RewritesState;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const open = state.open;
  const indexerId = state.open ? state.row.id : null;
  const indexerName = state.open ? state.row.name : "";

  const list = useQuery({
    queryKey: ["indexer-rewrites", indexerId],
    queryFn: () => indexersApi.rewritesList(indexerId as number),
    enabled: open && indexerId !== null,
    refetchOnWindowFocus: false,
  });

  const [draft, setDraft] = useState({ pattern: "", replacement: "", ord: 0 });
  const [editing, setEditing] = useState<{
    id: number;
    pattern: string;
    replacement: string;
    ord: number;
  } | null>(null);
  const [testInput, setTestInput] = useState("Mumford & Sons");

  // Reset transient state on each open.
  useEffect(() => {
    if (open) {
      setDraft({ pattern: "", replacement: "", ord: 0 });
      setEditing(null);
    }
  }, [open]);

  const create = useMutation({
    mutationFn: () =>
      indexersApi.rewritesCreate(indexerId as number, {
        pattern: draft.pattern,
        replacement: draft.replacement,
        ord: draft.ord,
      }),
    onSuccess: () => {
      toast("Rule added", "success");
      setDraft({ pattern: "", replacement: "", ord: 0 });
      qc.invalidateQueries({ queryKey: ["indexer-rewrites", indexerId] });
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : "Add failed", "error"),
  });

  const save = useMutation({
    mutationFn: () => {
      if (!editing) throw new Error("nothing to save");
      return indexersApi.rewritesUpdate(indexerId as number, editing.id, {
        pattern: editing.pattern,
        replacement: editing.replacement,
        ord: editing.ord,
      });
    },
    onSuccess: () => {
      toast("Rule updated", "success");
      setEditing(null);
      qc.invalidateQueries({ queryKey: ["indexer-rewrites", indexerId] });
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : "Save failed", "error"),
  });

  const remove = useMutation({
    mutationFn: (rid: number) =>
      indexersApi.rewritesDelete(indexerId as number, rid),
    onSuccess: () => {
      toast("Rule removed", "success");
      qc.invalidateQueries({ queryKey: ["indexer-rewrites", indexerId] });
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : "Remove failed", "error"),
  });

  // Local-only preview of what `testInput` becomes after the rules
  // apply. Mirrors `apply_rewrites` in the Rust side: each rule's
  // pattern is compiled as a JS regex and `replaceAll`-style global
  // replace runs. JS regex syntax is a strict subset of Rust's `regex`
  // crate for the constructs users will write here (character
  // classes, anchors, escapes), so the preview is faithful for the
  // common cases. If a pattern fails to compile in JS we render the
  // input untransformed and surface the parse error inline.
  const preview = useMemo(() => {
    const rules = list.data ?? [];
    let cur = testInput;
    for (const rule of rules) {
      try {
        const re = new RegExp(rule.pattern, "g");
        cur = cur.replace(re, rule.replacement);
      } catch (e) {
        return {
          value: testInput,
          error:
            e instanceof Error
              ? `rule #${rule.id} (${rule.pattern}): ${e.message}`
              : "preview failed",
        };
      }
    }
    return { value: cur, error: null as string | null };
  }, [list.data, testInput]);

  const submitDraft = (e: FormEvent) => {
    e.preventDefault();
    if (!draft.pattern.trim()) {
      toast("Pattern is required", "error");
      return;
    }
    create.mutate();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Query rewrites — ${indexerName}`}
      width="xl"
    >
      <div className="space-y-4">
        <p className="text-xs text-foreground/60">
          Regex find/replace rules applied to the outbound query, in order,
          just before this indexer is searched. Use cases: strip apostrophes,
          replace <code>&amp;</code> with <code>and</code>, append a suffix
          like <code>+VR</code> for adult-content trackers.
        </p>

        {list.isLoading ? (
          <p className="text-sm text-foreground/60">Loading…</p>
        ) : !list.data || list.data.length === 0 ? (
          <EmptyState
            title="No rewrites yet"
            description="Add a rule below to start rewriting queries to this indexer."
          />
        ) : (
          <div className="overflow-x-auto rounded border border-line">
            <table className="w-full text-sm">
              <thead className="bg-surface-2/50 text-left text-xs uppercase text-foreground/60">
                <tr>
                  <th className="px-3 py-2 font-medium">Order</th>
                  <th className="px-3 py-2 font-medium">Pattern</th>
                  <th className="px-3 py-2 font-medium">Replacement</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {list.data.map((rule) => (
                  <RewriteRow
                    key={rule.id}
                    rule={rule}
                    editing={editing?.id === rule.id ? editing : null}
                    onStartEdit={() =>
                      setEditing({
                        id: rule.id,
                        pattern: rule.pattern,
                        replacement: rule.replacement,
                        ord: rule.ord,
                      })
                    }
                    onCancelEdit={() => setEditing(null)}
                    onChangeEdit={(patch) =>
                      setEditing((cur) => (cur ? { ...cur, ...patch } : cur))
                    }
                    onSaveEdit={() => save.mutate()}
                    onRemove={() => {
                      if (
                        confirm(
                          `Remove rewrite "${rule.pattern}" → "${rule.replacement}"?`,
                        )
                      ) {
                        remove.mutate(rule.id);
                      }
                    }}
                    saving={save.isPending}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}

        <form
          onSubmit={submitDraft}
          className="space-y-3 rounded border border-line bg-surface-2/30 p-3"
        >
          <p className="text-xs font-medium uppercase tracking-wide text-foreground/60">
            Add rule
          </p>
          <div className="grid grid-cols-[1fr_1fr_80px_auto] gap-2">
            <Field label="Pattern (regex)">
              <TextInput
                value={draft.pattern}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, pattern: e.target.value }))
                }
                placeholder="\\s*&\\s*"
                spellCheck={false}
              />
            </Field>
            <Field label="Replacement">
              <TextInput
                value={draft.replacement}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, replacement: e.target.value }))
                }
                placeholder=" and "
              />
            </Field>
            <Field label="Order">
              <NumberInput
                value={draft.ord}
                onChange={(e) =>
                  setDraft((d) => ({
                    ...d,
                    ord: Number(e.target.value) || 0,
                  }))
                }
              />
            </Field>
            <div className="flex items-end">
              <Button type="submit" loading={create.isPending}>
                Add
              </Button>
            </div>
          </div>
        </form>

        <div className="space-y-2 rounded border border-line bg-surface-2/30 p-3">
          <p className="text-xs font-medium uppercase tracking-wide text-foreground/60">
            Test
          </p>
          <Field
            label="Input"
            hint="Type any string; the box below shows what would be sent to the indexer."
          >
            <TextInput
              value={testInput}
              onChange={(e) => setTestInput(e.target.value)}
              placeholder="your-query"
              spellCheck={false}
            />
          </Field>
          {preview.error ? (
            <p className="font-mono text-xs text-red-400">
              {preview.error}
            </p>
          ) : (
            <div className="rounded border border-line bg-background/40 px-3 py-2 font-mono text-sm">
              {preview.value || (
                <span className="text-foreground/40">(empty)</span>
              )}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

function RewriteRow({
  rule,
  editing,
  onStartEdit,
  onCancelEdit,
  onChangeEdit,
  onSaveEdit,
  onRemove,
  saving,
}: {
  rule: IndexerQueryRewriteRow;
  editing: {
    id: number;
    pattern: string;
    replacement: string;
    ord: number;
  } | null;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onChangeEdit: (
    patch: Partial<{ pattern: string; replacement: string; ord: number }>,
  ) => void;
  onSaveEdit: () => void;
  onRemove: () => void;
  saving: boolean;
}) {
  if (editing) {
    return (
      <tr className="border-t border-line align-top">
        <td className="px-3 py-2">
          <NumberInput
            value={editing.ord}
            onChange={(e) =>
              onChangeEdit({ ord: Number(e.target.value) || 0 })
            }
          />
        </td>
        <td className="px-3 py-2">
          <TextInput
            value={editing.pattern}
            onChange={(e) => onChangeEdit({ pattern: e.target.value })}
            spellCheck={false}
          />
        </td>
        <td className="px-3 py-2">
          <TextInput
            value={editing.replacement}
            onChange={(e) => onChangeEdit({ replacement: e.target.value })}
          />
        </td>
        <td className="space-x-2 px-3 py-2 text-right">
          <Button
            size="sm"
            onClick={onSaveEdit}
            loading={saving}
          >
            Save
          </Button>
          <Button size="sm" variant="ghost" onClick={onCancelEdit}>
            Cancel
          </Button>
        </td>
      </tr>
    );
  }
  return (
    <tr className="border-t border-line align-top">
      <td className="px-3 py-2 font-mono text-xs tabular-nums">{rule.ord}</td>
      <td className="px-3 py-2 font-mono text-xs">{rule.pattern}</td>
      <td className="px-3 py-2 font-mono text-xs">
        {rule.replacement || (
          <span className="text-foreground/40">(empty)</span>
        )}
      </td>
      <td className="space-x-2 px-3 py-2 text-right">
        <Button size="sm" variant="secondary" onClick={onStartEdit}>
          Edit
        </Button>
        <Button size="sm" variant="danger" onClick={onRemove}>
          Remove
        </Button>
      </td>
    </tr>
  );
}

/**
 * Per-indexer download-URL rewrites modal (#42).
 *
 * Same shape as the query-rewrites modal but the rules apply to each
 * release's `download_url` just before the download client / proxy
 * fetches it. Useful when an indexer returns a CDN URL that breaks
 * for users behind certain VPNs, or a tracker URL that needs proxy
 * substitution. The "Test" affordance shows what an arbitrary URL
 * would become after the full rule chain runs — same chain the
 * dispatch path runs on the wire.
 */
function IndexerDownloadRewritesModal({
  state,
  onClose,
}: {
  state: DownloadRewritesState;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const open = state.open;
  const indexerId = state.open ? state.row.id : null;
  const indexerName = state.open ? state.row.name : "";

  const list = useQuery({
    queryKey: ["indexer-download-rewrites", indexerId],
    queryFn: () => indexersApi.downloadRewritesList(indexerId as number),
    enabled: open && indexerId !== null,
    refetchOnWindowFocus: false,
  });

  const [draft, setDraft] = useState({ pattern: "", replacement: "", ord: 0 });
  const [editing, setEditing] = useState<{
    id: number;
    pattern: string;
    replacement: string;
    ord: number;
  } | null>(null);
  const [testInput, setTestInput] = useState(
    "https://cdn.example.com/api?id=42",
  );

  // Reset transient state on each open.
  useEffect(() => {
    if (open) {
      setDraft({ pattern: "", replacement: "", ord: 0 });
      setEditing(null);
    }
  }, [open]);

  const create = useMutation({
    mutationFn: () =>
      indexersApi.downloadRewritesCreate(indexerId as number, {
        pattern: draft.pattern,
        replacement: draft.replacement,
        ord: draft.ord,
      }),
    onSuccess: () => {
      toast("Rule added", "success");
      setDraft({ pattern: "", replacement: "", ord: 0 });
      qc.invalidateQueries({
        queryKey: ["indexer-download-rewrites", indexerId],
      });
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : "Add failed", "error"),
  });

  const save = useMutation({
    mutationFn: () => {
      if (!editing) throw new Error("nothing to save");
      return indexersApi.downloadRewritesUpdate(
        indexerId as number,
        editing.id,
        {
          pattern: editing.pattern,
          replacement: editing.replacement,
          ord: editing.ord,
        },
      );
    },
    onSuccess: () => {
      toast("Rule updated", "success");
      setEditing(null);
      qc.invalidateQueries({
        queryKey: ["indexer-download-rewrites", indexerId],
      });
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : "Save failed", "error"),
  });

  const remove = useMutation({
    mutationFn: (rid: number) =>
      indexersApi.downloadRewritesDelete(indexerId as number, rid),
    onSuccess: () => {
      toast("Rule removed", "success");
      qc.invalidateQueries({
        queryKey: ["indexer-download-rewrites", indexerId],
      });
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : "Remove failed", "error"),
  });

  // Local-only preview of what `testInput` becomes after the rules
  // apply. Mirrors the Rust side's `apply_rewrites`. JS regex syntax
  // is a strict subset of Rust's `regex` crate for the constructs
  // users will write here, so the preview is faithful for the common
  // host-swap / param-strip cases.
  const preview = useMemo(() => {
    const rules = list.data ?? [];
    let cur = testInput;
    for (const rule of rules) {
      try {
        const re = new RegExp(rule.pattern, "g");
        cur = cur.replace(re, rule.replacement);
      } catch (e) {
        return {
          value: testInput,
          error:
            e instanceof Error
              ? `rule #${rule.id} (${rule.pattern}): ${e.message}`
              : "preview failed",
        };
      }
    }
    return { value: cur, error: null as string | null };
  }, [list.data, testInput]);

  const submitDraft = (e: FormEvent) => {
    e.preventDefault();
    if (!draft.pattern.trim()) {
      toast("Pattern is required", "error");
      return;
    }
    create.mutate();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Download URL rewrites — ${indexerName}`}
      width="xl"
    >
      <div className="space-y-4">
        <p className="text-xs text-foreground/60">
          Regex find/replace rules applied to each release's{" "}
          <code>download_url</code>, in order, just before the download
          client (or NZB proxy) fetches it. Use cases: swap a CDN host
          that's blocked for users on certain VPNs, inject a self-hosted
          reverse proxy, strip a query param the client chokes on.
        </p>

        {list.isLoading ? (
          <p className="text-sm text-foreground/60">Loading…</p>
        ) : !list.data || list.data.length === 0 ? (
          <EmptyState
            title="No download-URL rewrites yet"
            description="Add a rule below to start rewriting download URLs from this indexer."
          />
        ) : (
          <div className="overflow-x-auto rounded border border-line">
            <table className="w-full text-sm">
              <thead className="bg-surface-2/50 text-left text-xs uppercase text-foreground/60">
                <tr>
                  <th className="px-3 py-2 font-medium">Order</th>
                  <th className="px-3 py-2 font-medium">Pattern</th>
                  <th className="px-3 py-2 font-medium">Replacement</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {list.data.map((rule) => (
                  <DownloadRewriteRow
                    key={rule.id}
                    rule={rule}
                    editing={editing?.id === rule.id ? editing : null}
                    onStartEdit={() =>
                      setEditing({
                        id: rule.id,
                        pattern: rule.pattern,
                        replacement: rule.replacement,
                        ord: rule.ord,
                      })
                    }
                    onCancelEdit={() => setEditing(null)}
                    onChangeEdit={(patch) =>
                      setEditing((cur) => (cur ? { ...cur, ...patch } : cur))
                    }
                    onSaveEdit={() => save.mutate()}
                    onRemove={() => {
                      if (
                        confirm(
                          `Remove rewrite "${rule.pattern}" → "${rule.replacement}"?`,
                        )
                      ) {
                        remove.mutate(rule.id);
                      }
                    }}
                    saving={save.isPending}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}

        <form
          onSubmit={submitDraft}
          className="space-y-3 rounded border border-line bg-surface-2/30 p-3"
        >
          <p className="text-xs font-medium uppercase tracking-wide text-foreground/60">
            Add rule
          </p>
          <div className="grid grid-cols-[1fr_1fr_80px_auto] gap-2">
            <Field label="Pattern (regex)">
              <TextInput
                value={draft.pattern}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, pattern: e.target.value }))
                }
                placeholder="^https://cdn\\.example\\.com"
                spellCheck={false}
              />
            </Field>
            <Field label="Replacement">
              <TextInput
                value={draft.replacement}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, replacement: e.target.value }))
                }
                placeholder="https://proxy.local"
              />
            </Field>
            <Field label="Order">
              <NumberInput
                value={draft.ord}
                onChange={(e) =>
                  setDraft((d) => ({
                    ...d,
                    ord: Number(e.target.value) || 0,
                  }))
                }
              />
            </Field>
            <div className="flex items-end">
              <Button type="submit" loading={create.isPending}>
                Add
              </Button>
            </div>
          </div>
        </form>

        <div className="space-y-2 rounded border border-line bg-surface-2/30 p-3">
          <p className="text-xs font-medium uppercase tracking-wide text-foreground/60">
            Test
          </p>
          <Field
            label="Input URL"
            hint="Paste a sample download_url; the box below shows what would be fetched after rewriting."
          >
            <TextInput
              value={testInput}
              onChange={(e) => setTestInput(e.target.value)}
              placeholder="https://indexer.example/dl?id=42"
              spellCheck={false}
            />
          </Field>
          {preview.error ? (
            <p className="font-mono text-xs text-red-400">
              {preview.error}
            </p>
          ) : (
            <div className="rounded border border-line bg-background/40 px-3 py-2 font-mono text-sm break-all">
              {preview.value || (
                <span className="text-foreground/40">(empty)</span>
              )}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

function DownloadRewriteRow({
  rule,
  editing,
  onStartEdit,
  onCancelEdit,
  onChangeEdit,
  onSaveEdit,
  onRemove,
  saving,
}: {
  rule: IndexerDownloadRewriteRow;
  editing: {
    id: number;
    pattern: string;
    replacement: string;
    ord: number;
  } | null;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onChangeEdit: (
    patch: Partial<{ pattern: string; replacement: string; ord: number }>,
  ) => void;
  onSaveEdit: () => void;
  onRemove: () => void;
  saving: boolean;
}) {
  if (editing) {
    return (
      <tr className="border-t border-line align-top">
        <td className="px-3 py-2">
          <NumberInput
            value={editing.ord}
            onChange={(e) =>
              onChangeEdit({ ord: Number(e.target.value) || 0 })
            }
          />
        </td>
        <td className="px-3 py-2">
          <TextInput
            value={editing.pattern}
            onChange={(e) => onChangeEdit({ pattern: e.target.value })}
            spellCheck={false}
          />
        </td>
        <td className="px-3 py-2">
          <TextInput
            value={editing.replacement}
            onChange={(e) => onChangeEdit({ replacement: e.target.value })}
          />
        </td>
        <td className="space-x-2 px-3 py-2 text-right">
          <Button size="sm" onClick={onSaveEdit} loading={saving}>
            Save
          </Button>
          <Button size="sm" variant="ghost" onClick={onCancelEdit}>
            Cancel
          </Button>
        </td>
      </tr>
    );
  }
  return (
    <tr className="border-t border-line align-top">
      <td className="px-3 py-2 font-mono text-xs tabular-nums">{rule.ord}</td>
      <td className="px-3 py-2 font-mono text-xs break-all">{rule.pattern}</td>
      <td className="px-3 py-2 font-mono text-xs break-all">
        {rule.replacement || (
          <span className="text-foreground/40">(empty)</span>
        )}
      </td>
      <td className="space-x-2 px-3 py-2 text-right">
        <Button size="sm" variant="secondary" onClick={onStartEdit}>
          Edit
        </Button>
        <Button size="sm" variant="danger" onClick={onRemove}>
          Remove
        </Button>
      </td>
    </tr>
  );
}
